import {ChangeDetectorRef, Component, ElementRef, OnInit, ViewChild} from '@angular/core';
import {DataService} from '../../../../Services/data.service';
import {StorageService} from '../../../../Services/storage.service';
import {Title} from '@angular/platform-browser';
import {Router} from '@angular/router';
import {BsToastService} from '../../../../Services/bs-toast-service';
import {ReportPrintService} from '../../../../Services/report-print.service';
import {SendEmailModalComponent} from '../../../common/send-email-modal/send-email-modal.component';
import {EmailModel} from '../../../../DataModels/email-model';
import {Modal} from 'bootstrap';
import {DateFilterService} from '../../../../Services/dateFilter.service';

export interface ProjectTimeReportRow {
  project_id: string;
  project_number: string;
  project_name: string;
  entity_name: string;
  user_id: string;
  entry_date: string;
  task_name: string;
  hours: number;
  billable: boolean;
  bill_rate: number | null;
  bill_amount: number | null;
  notes: string;
}

export interface ProjectTimeGroup {
  project_id: string;
  project_number: string;
  project_name: string;
  entity_name: string;
  users: ProjectTimeUserGroup[];
  totalHours: number;
  totalBillableHours: number;
  totalNonBillableHours: number;
  totalBillAmount: number;
}

export interface ProjectTimeUserGroup {
  user_id: string;
  entries: ProjectTimeReportRow[];
  totalHours: number;
  billableHours: number;
  nonBillableHours: number;
  billAmount: number;
}

@Component({
  selector: 'app-project-time-report',
  templateUrl: './project-time-report.component.html',
  styleUrls: [],
  standalone: false,
})
export class ProjectTimeReportComponent implements OnInit {

  @ViewChild('ReportOutput', {static: false}) ReportOutput: ElementRef;
  @ViewChild(SendEmailModalComponent, {read: ElementRef}) private localSendEmailModalHost: ElementRef;

  rptName = 'Time by Project Report';
  isLoading = false;
  noDataFound = false;
  rows: ProjectTimeReportRow[] = [];
  groups: ProjectTimeGroup[] = [];
  grandTotalHours = 0;
  grandTotalBillAmount = 0;
  projects: any[] = [];
  entities: any[] = [];
  users: any[] = [];
  reportDataToSendInEmail: EmailModel = new EmailModel();

  buttonConfig: any = {
    print: false,
    printReport: true,
    email: true,
    update: true,
    exportExcel: false,
    close: true,
    clear: false,
    filters: false,
    reset: true,
  };

  filters: any = {
    company_id: '',
    project_id: '',
    entity_id: '',
    user_id: '',
    from_date: '',
    to_date: '',
    billable: '',
  };

  billableOptions = [
    {value: '', label: 'All'},
    {value: 'true', label: 'Billable Only'},
    {value: 'false', label: 'Non-Billable Only'},
  ];

  vm: any = {
    Preferences: [],
  };

  constructor(
    private titleService: Title,
    private router: Router,
    private tableDataService: DataService,
    private localStorage: StorageService,
    private notification: BsToastService,
    private reportPrint: ReportPrintService,
    private cd: ChangeDetectorRef,
    private dateFilter: DateFilterService,
  ) {
  }

  ngOnInit() {
    this.titleService.setTitle(this.rptName);
    this.vm.Preferences = JSON.parse(this.localStorage.StorageGet('Preferences'));
    this.filters.company_id = this.vm.Preferences?.defaults?.company_id ?? '';

    this.tableDataService.getApiListData('sys_user', '?pageNumber=0&pageSize=999').subscribe({
      next: (result: any) => {
        this.users = result.Data ?? result.items ?? [];
      },
    });
    this.tableDataService.getDataWithParams('project', 'pageNumber=0&pageSize=999').subscribe({
      next: (result: any) => {
        this.projects = Array.isArray(result) ? result : (result.Data ?? result.items ?? []);
      },
    });
    this.tableDataService.getApiListData('entity', '?pageNumber=0&pageSize=999').subscribe({
      next: (result: any) => {
        this.entities = result.Data ?? result.items ?? [];
      },
    });
  }

  getUserName(userId: string): string {
    if (!userId) {
      return '—';
    }
    const u = this.users.find(x => x.id === userId);
    return u ? u.name : userId;
  }

  resetFilters() {
    this.filters = {
      company_id: this.vm.Preferences?.defaults?.company_id ?? '',
      project_id: '',
      entity_id: '',
      user_id: '',
      from_date: '',
      to_date: '',
      billable: '',
    };
    this.rows = [];
    this.groups = [];
    this.vm.report = [];
  }

  close() {
    this.router.navigate(['/home']);
  }

  private buildGroups(rows: ProjectTimeReportRow[]): ProjectTimeGroup[] {
    const projectMap = new Map<string, ProjectTimeGroup>();
    for (const row of rows) {
      if (!projectMap.has(row.project_id)) {
        projectMap.set(row.project_id, {
          project_id: row.project_id,
          project_number: row.project_number,
          project_name: row.project_name,
          entity_name: row.entity_name,
          users: [],
          totalHours: 0,
          totalBillableHours: 0,
          totalNonBillableHours: 0,
          totalBillAmount: 0,
        });
      }
      const pg = projectMap.get(row.project_id)!;
      let ug = pg.users.find(u => u.user_id === row.user_id);
      if (!ug) {
        ug = {user_id: row.user_id, entries: [], totalHours: 0, billableHours: 0, nonBillableHours: 0, billAmount: 0};
        pg.users.push(ug);
      }
      ug.entries.push(row);
      ug.totalHours += row.hours;
      ug.billAmount += row.bill_amount ?? 0;
      if (row.billable) {
        ug.billableHours += row.hours;
      } else {
        ug.nonBillableHours += row.hours;
      }
      pg.totalHours += row.hours;
      pg.totalBillAmount += row.bill_amount ?? 0;
      if (row.billable) {
        pg.totalBillableHours += row.hours;
      } else {
        pg.totalNonBillableHours += row.hours;
      }
    }
    return Array.from(projectMap.values());
  }

  runReport() {
    this.isLoading = true;
    this.noDataFound = false;
    this.rows = [];
    this.groups = [];

    const params: string[] = [];
    if (this.filters.company_id) {
      params.push('company_id=' + this.filters.company_id);
    }
    if (this.filters.project_id) {
      params.push('project_id=' + this.filters.project_id);
    }
    if (this.filters.entity_id) {
      params.push('entity_id=' + this.filters.entity_id);
    }
    if (this.filters.user_id) {
      params.push('user_id=' + this.filters.user_id);
    }
    const fromDate = this.filters.from_date ? this.dateFilter.GetDateFormat(this.filters.from_date) : '';
    const toDate = this.filters.to_date ? this.dateFilter.GetDateFormat(this.filters.to_date) : '';
    if (fromDate) {
      params.push('from_date=' + fromDate);
    }
    if (toDate) {
      params.push('to_date=' + toDate);
    }
    if (this.filters.billable !== '') {
      params.push('billable=' + this.filters.billable);
    }

    this.tableDataService.getDataWithParams('ProjectTimeReport', params.join('&')).subscribe({
      next: (result: any) => {
        this.rows = Array.isArray(result) ? result : [];
        this.groups = this.buildGroups(this.rows);
        this.grandTotalHours = this.rows.reduce((s, r) => s + r.hours, 0);
        this.grandTotalBillAmount = this.rows.reduce((s, r) => s + (r.bill_amount ?? 0), 0);
        this.vm.report = this.rows;
        this.noDataFound = this.rows.length === 0;
        this.isLoading = false;
      },
      error: (err) => {
        console.error(err);
        this.notification.showErrorToast('Error loading report.');
        this.isLoading = false;
      },
    });
  }

  printReport() {
    try {
      const html = this.buildPrintHtml();
      this.reportPrint.printInIframe(html);
    } catch (e) {
      console.error('Time by Project Report print failed', e);
    }
  }

  setEmailData() {
    const emailDocObj = {} as EmailModel;
    emailDocObj.print_orientation = '1';
    emailDocObj.convert_attachment_to = 'pdf';
    emailDocObj.fromEmail = '';
    emailDocObj.fromName = 'AcctXErp';
    emailDocObj.header_id = '';
    emailDocObj.toEmail = '';
    emailDocObj.body = 'Please see the attached ' + this.rptName + '.<br><br>Best regards,<br><br>AcctXErp<br><br>';
    emailDocObj.Subject = 'AcctXErp ' + this.rptName;
    try {
      this.cd.detectChanges();
    } catch {
    }
    try {
      emailDocObj.pdfHtml = this.buildPrintHtml(true);
    } catch {
      emailDocObj.pdfHtml = this.ReportOutput?.nativeElement?.outerHTML || '';
    }
    emailDocObj.activity_message = this.rptName;
    const now = new Date();
    emailDocObj.FileName = `TimeByProject_${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}.pdf`;
    this.reportDataToSendInEmail = emailDocObj;
    const element = document.getElementById('sendEmailModal') as HTMLElement;
    if (element) {
      const emailModal = new Modal(element);
      emailModal.show();
    }
  }

  private buildPrintHtml(forEmail: boolean = false): string {
    const rawContent = (this.ReportOutput?.nativeElement?.outerHTML || '').toString();
    const content = this.reportPrint.cleanContentHtml(rawContent);
    const meta: { label: string; value: string }[] = [];
    if (this.filters.from_date || this.filters.to_date) {
      meta.push({label: 'Date Range', value: [this.filters.from_date, this.filters.to_date].filter(Boolean).join(' to ')});
    }
    return this.reportPrint.buildHtml({title: this.rptName, contentHtml: content, meta, orientation: 'landscape'});
  }
}
